/* Cookie consent banner — drives the Shopify Customer Privacy API.
 * Shows only in collect-after-consent regions (config in Shopify admin),
 * pushes consent state to dataLayer as `kumiko_consent_update` for GTM.
 *
 * Persistence: Shopify's consentManagement mutation succeeds but does not
 * always flip the server-baked `display_banner` flag (nor persist
 * _tracking_consent) for every store/region configuration. Like third-party
 * CMPs, we keep our own decision cookie and re-assert the saved decision on
 * every page load so Shopify's pixel gating and our GTM signals stay aligned. */
(() => {
  const DATA_LAYER_EVENT = 'kumiko_consent_update';
  const DECISION_COOKIE = 'kumiko_consent_v1';
  const DECISION_MAX_AGE = 15552000; // 180 days, mirrors Shopify's consent TTL

  const banner = document.getElementById('ConsentBanner');
  if (!banner) return;

  const dataLayer = (window.dataLayer = window.dataLayer || []);

  const pushConsentEvent = (consent) => {
    dataLayer.push({
      event: DATA_LAYER_EVENT,
      consent_analytics: consent.analytics,
      consent_marketing: consent.marketing,
      consent_preferences: consent.preferences,
    });
  };

  const readDecision = () => {
    const raw = document.cookie.split('; ').find((c) => c.startsWith(DECISION_COOKIE + '='));
    if (!raw) return null;
    try {
      const value = JSON.parse(decodeURIComponent(raw.split('=')[1] || ''));
      if (typeof value.analytics !== 'boolean' || typeof value.marketing !== 'boolean') return null;
      return value;
    } catch {
      return null;
    }
  };

  const writeDecision = (consent, saleOfData) => {
    const value = encodeURIComponent(JSON.stringify({ ...consent, sale_of_data: saleOfData === true }));
    document.cookie = `${DECISION_COOKIE}=${value}; path=/; max-age=${DECISION_MAX_AGE}; samesite=lax`;
  };

  /* Consent travels with the cart into checkout attributes and order
   * note_attributes: it survives payment-redirect browser switches and gives
   * the orders/paid webhook per-order consent evidence. Fire-and-forget. */
  const persistToCart = (consent) => {
    try {
      fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          attributes: {
            'Consent marketing': consent.marketing ? 'granted' : 'denied',
            'Consent analytics': consent.analytics ? 'granted' : 'denied',
            'Consent updated at': new Date().toISOString(),
          },
        }),
        keepalive: true,
      }).catch(() => {});
    } catch {
      /* cart unavailable (e.g. bot): non-blocking */
    }
  };

  const show = () => {
    banner.hidden = false;
    banner.focus();
  };

  const hide = () => {
    banner.hidden = true;
  };

  const onApiReady = () => {
    const consentApi = window.Shopify && window.Shopify.customerPrivacy;
    if (!consentApi) return;

  const applyConsent = (consent, saleOfData) => {
    // sale_of_data must be a Boolean in the storefront VisitorConsent input.
    const withSale = { ...consent, sale_of_data: saleOfData === true };
    const done = () => {
      writeDecision(consent, saleOfData);
      pushConsentEvent(consent);
      persistToCart(consent);
      hide();
    };
    // Granular consent stores keep shouldShowBanner() true until every required
    // purpose has a decision. Some storefront API versions reject purposes
    // outside the visitor's regulated region, so fall back to the core purposes
    // and log the API's exact complaint with the resolved region context.
    consentApi.setTrackingConsent(withSale, (result) => {
      if (result && result.error) {
        console.error('Consent banner: setTrackingConsent rejected', withSale, result.error, {
          locale: window.Shopify && window.Shopify.locale,
          country: window.Shopify && window.Shopify.country,
          region: typeof consentApi.getRegion === 'function' ? consentApi.getRegion() : null,
        });
        consentApi.setTrackingConsent(consent, (retry) => {
          if (retry && retry.error) {
            console.error('Consent banner: core purposes retry failed', retry.error);
            return;
          }
          done();
        });
        return;
      }
      done();
    });
  };

  banner.querySelector('[data-consent-accept]').addEventListener('click', () => {
    applyConsent({ analytics: true, marketing: true, preferences: true }, true);
  });

  banner.querySelector('[data-consent-decline]').addEventListener('click', () => {
    applyConsent({ analytics: false, marketing: false, preferences: false }, false);
  });

  // Granular panel: audience measurement and advertising decided separately.
  // Data sale follows the advertising choice (ad sharing is what "sale" means
  // under US privacy law). Functional preferences have no third-party scope
  // here and follow the accept-all default.
  const prefsPanel = banner.querySelector('#ConsentBannerPrefs');
  const customizeToggle = banner.querySelector('[data-consent-customize-toggle]');
  if (customizeToggle && prefsPanel) {
    customizeToggle.addEventListener('click', () => {
      const open = prefsPanel.hidden;
      prefsPanel.hidden = !open;
      customizeToggle.setAttribute('aria-expanded', String(open));
    });
    banner.querySelector('[data-consent-save]').addEventListener('click', () => {
      const analytics = banner.querySelector('[data-consent-pref-analytics]').checked;
      const marketing = banner.querySelector('[data-consent-pref-marketing]').checked;
      applyConsent({ analytics, marketing, preferences: true }, marketing);
    });
  }

    banner.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') hide();
    });

    // Re-assert a previously saved decision when Shopify lost the visitor
    // state (the consentManagement mutation can succeed without persisting
    // _tracking_consent). Silent: no banner, just the API + dataLayer sync.
    const saved = readDecision();
    const current = typeof consentApi.currentVisitorConsent === 'function'
      ? consentApi.currentVisitorConsent() || {}
      : {};
    const shopifyUndecided = Object.values(current).every((v) => v === '' || v === undefined);
    if (saved && shopifyUndecided) {
      consentApi.setTrackingConsent(saved, () => {});
      pushConsentEvent(saved);
    }

    const undecidedHere = !saved && shopifyUndecided;
    if (undecidedHere && typeof consentApi.shouldShowBanner === 'function' && consentApi.shouldShowBanner()) {
      show();
    }

    // Global Privacy Control: opt out of data sale in US privacy regions.
    try {
      const region = typeof consentApi.getRegion === 'function' ? consentApi.getRegion() : '';
      if (navigator.globalPrivacyControl && /^US-(CA|VA|CT|UT|CO)/.test(region)) {
        consentApi.setTrackingConsent({ sale_of_data: false }, () => {});
      }
    } catch (error) {
      console.error('Consent banner: GPC handling failed', error);
    }
  };

  // Report every consent change (banner or other surfaces) to the dataLayer.
  document.addEventListener('visitorConsentCollected', (event) => {
    const detail = event.detail || {};
    pushConsentEvent({
      analytics: detail.analyticsAllowed === true,
      marketing: detail.marketingAllowed === true,
      preferences: detail.preferencesAllowed === true,
    });
  });

  document.querySelectorAll('[data-consent-reopen]').forEach((trigger) => {
    trigger.addEventListener('click', (event) => {
      event.preventDefault();
      show();
    });
  });

  window.KumikoConsent = { show };

  // The granular-consent storefront API (v0.2) carries the consentManagement
  // settings the v0.1 shim no longer receives; loading 0.1 first crashed with
  // "Cannot read properties of undefined (reading 'consentManagement')".
  const loadApi = (version, fallbackVersion) => {
    window.Shopify.loadFeatures([{ name: 'consent-tracking-api', version }], (error) => {
      if (error || !window.Shopify.customerPrivacy) {
        // "already been loaded at version 0.1" is normal when another script
        // (analytics, apps) bootstrapped the API first — fall back silently.
        if (!error || !/already been loaded/.test(String(error.message || error))) {
          console.error('Consent banner: unable to load the consent API v' + version, error || 'api object missing');
        }
        if (fallbackVersion) loadApi(fallbackVersion);
        return;
      }
      onApiReady();
    });
  };

  if (window.Shopify && window.Shopify.customerPrivacy) {
    onApiReady();
  } else if (window.Shopify && window.Shopify.loadFeatures) {
    loadApi('0.2', '0.1');
  }
})();
